import { redactFunctionKey } from "./redactFunctionKey";

interface ApiErrorPayload {
  message?: string;
  errors?: Record<string, string[]>;
}

function normalizeUrl(baseUrl: string, path: string): string {
  if (/^https?:\/\//i.test(path)) {
    return path;
  }

  return `${baseUrl.replace(/\/$/, "")}/${path.replace(/^\//, "")}`;
}

async function parseJsonResponse<T>(response: Response): Promise<T | string | undefined> {
  const text = await response.text();

  if (!text) {
    return undefined;
  }

  try {
    return JSON.parse(text) as T;
  } catch {
    // If body is not JSON, return raw text so callers can extract error messages
    return text;
  }
}

function formatApiError(payload: ApiErrorPayload | string | undefined, fallbackStatus: number): string {
  if (typeof payload === "string") {
    const trimmed = payload.trim();
    if (trimmed) return trimmed;
  }

  const message = (payload as ApiErrorPayload | undefined)?.message?.trim();

  if ((payload as ApiErrorPayload | undefined)?.errors && Object.keys((payload as ApiErrorPayload).errors!).length > 0) {
    const detail = Object.entries((payload as ApiErrorPayload).errors!)
      .map(([field, messages]) => `${field}: ${messages.join(", ")}`)
      .join(" | ");

    return message ? `${message} (${detail})` : detail;
  }

  if (message) {
    return message;
  }

  // A body of some other shape still says more than the status does. Showing it is not pretty, but
  // losing it means a report of "Request failed with status 400" and nothing to go on.
  if (payload && typeof payload === "object") {
    try {
      const serialised = JSON.stringify(payload);
      if (serialised && serialised !== "{}") {
        return `Request failed with status ${fallbackStatus}: ${serialised}`;
      }
    } catch {
      // Circular or otherwise unserialisable - the status alone will have to do.
    }
  }

  return `Request failed with status ${fallbackStatus}.`;
}

/**
 * Turns a rejected response into the error the screen will show.
 *
 * `serverMessage` deliberately carries the *formatted* text rather than the response body. It used
 * to be the body verbatim - "exact 1:1 so UI can display it" - which is what put
 * `{"message":"Dein Konto ist keinem Team zugeordnet."}` on screen, braces and all: every banner
 * reads `serverMessage` first, and a stringified object is never empty, so `formatApiError` below
 * was written but never reached. The untouched body is still available in `payload` for the
 * console, which is the only place it was ever useful.
 */
function rejected(result: unknown, response: Response, url: string): ApiError {
  const message = formatApiError(result as ApiErrorPayload | string | undefined, response.status);

  return new ApiError(message, { serverMessage: message, status: response.status, url, payload: result });
}

type FunctionService = "game" | "accounts" | "analytics";

function getFunctionBaseForService(service?: FunctionService): string {
  if (service === "game") {
    return import.meta.env.VITE_FUNCTIONS_GAME_BASE?.trim() || import.meta.env.VITE_FUNCTIONS_BASE_URL?.trim() || "/api";
  }

  if (service === "accounts") {
    return import.meta.env.VITE_FUNCTIONS_ACCOUNTS_BASE?.trim() || import.meta.env.VITE_FUNCTIONS_BASE_URL?.trim() || "/api";
  }

  if (service === "analytics") {
    return import.meta.env.VITE_FUNCTIONS_ANALYTICS_BASE?.trim() || import.meta.env.VITE_FUNCTIONS_BASE_URL?.trim() || "/api";
  }

  return import.meta.env.VITE_FUNCTIONS_BASE_URL?.trim() || "/api";
}

// Each function app has its own host key. Sending one app's key to another
// yields 401, so the key has to be picked per service.
function getFunctionKeyForService(service?: FunctionService): string | undefined {
  const sharedKey = import.meta.env.VITE_FUNCTIONS_KEY?.trim();

  if (service === "game") {
    return import.meta.env.VITE_FUNCTIONS_GAME_KEY?.trim() || sharedKey;
  }

  if (service === "accounts") {
    return import.meta.env.VITE_FUNCTIONS_ACCOUNTS_KEY?.trim() || sharedKey;
  }

  if (service === "analytics") {
    return import.meta.env.VITE_FUNCTIONS_ANALYTICS_KEY?.trim() || sharedKey;
  }

  return sharedKey;
}

// The host key travels as the x-functions-key header, the way the Flutter app sends it. It
// used to be appended as ?code=<key>, which wrote it into the request log of every function
// app. The platform CORS of the function apps answers a preflight for any requested header,
// and the bearer token makes nearly every call a preflighted one anyway.
function buildHeaders(
  accessToken: string | null | undefined,
  service: FunctionService | undefined,
  jsonContentType: boolean,
): Record<string, string> {
  const headers: Record<string, string> = {};

  if (jsonContentType) {
    headers["Content-Type"] = "application/json";
  }

  if (accessToken) {
    headers.Authorization = `Bearer ${accessToken}`;
  }

  const functionKey = getFunctionKeyForService(service);
  if (functionKey) {
    headers["x-functions-key"] = functionKey;
  }

  return headers;
}

export function buildFunctionUrl(route: string, service?: FunctionService): string {
  return normalizeUrl(getFunctionBaseForService(service), route);
}

export class ApiError extends Error {
  public serverMessage?: string | undefined;
  public status?: number | undefined;
  public url?: string | undefined;
  public isNetwork?: boolean | undefined;
  public payload?: unknown;

  constructor(message: string, opts?: { serverMessage?: string; status?: number; url?: string; isNetwork?: boolean; payload?: unknown }) {
    // Redacted here rather than at every throw site: the message and the url both end up on
    // screen. Requests no longer put the host key into the url, but a url that still carries
    // ?code=<key> - an endpoint override written the old way - must not hand it to whoever
    // reads the banner.
    super(redactFunctionKey(message));
    this.name = "ApiError";
    if (opts) {
      this.serverMessage = opts.serverMessage;
      this.status = opts.status;
      this.url = opts.url === undefined ? undefined : redactFunctionKey(opts.url);
      this.isNetwork = opts.isNetwork;
      this.payload = opts.payload;
    }
  }
}

export async function postFunctionJson<TResponse>(
  route: string,
  payload: unknown,
  accessToken?: string | null,
  service?: FunctionService,
): Promise<TResponse | undefined> {
  const url = buildFunctionUrl(route, service);
  let response: Response;
  try {
    response = await fetch(url, {
      method: "POST",
      headers: buildHeaders(accessToken, service, true),
      body: JSON.stringify(payload),
    });
  } catch (err) {
    const errMsg = err instanceof Error ? err.message : String(err);
    throw new ApiError(`Der Dienst ist nicht erreichbar: ${url} (${errMsg})`, { isNetwork: true, url, payload: err });
  }

  const result = await parseJsonResponse<TResponse & ApiErrorPayload>(response);

  if (!response.ok) {
    throw rejected(result, response, url);
  }

  // An endpoint that only confirms - taking a player out of the squad, storing a schedule -
  // answers 204 with nothing in it. Treating that as a fault put "Response body is empty." on
  // screen while the change had in fact gone through, so the caller fell back to its own
  // wording for nothing. The return type has always admitted undefined; now it means it.
  if (result === undefined) {
    return undefined;
  }

  if (typeof result === "string") {
    // Server returned non-JSON successful response; surface raw text as error
    throw new ApiError(result, { serverMessage: result, status: response.status, url });
  }

  return result as TResponse;
}

export async function deleteFunction(
  route: string,
  accessToken?: string | null,
  service?: FunctionService,
): Promise<void> {
  const url = buildFunctionUrl(route, service);
  let response: Response;
  try {
    response = await fetch(url, {
      method: "DELETE",
      headers: buildHeaders(accessToken, service, true),
    });
  } catch (err) {
    const errMsg = err instanceof Error ? err.message : String(err);
    throw new ApiError(`Der Dienst ist nicht erreichbar: ${url} (${errMsg})`, { isNetwork: true, url, payload: err });
  }

  if (!response.ok) {
    const result = await parseJsonResponse<ApiErrorPayload>(response);
    throw rejected(result, response, url);
  }

  // A successful delete answers 204 without a body - nothing to parse.
}

/**
 * Like {@link deleteFunction}, but for an endpoint that answers with a body. Deleting a
 * season reports how much went with it, and that number belongs on screen rather than in
 * a discarded response.
 */
export async function deleteFunctionJson<TResponse>(
  route: string,
  accessToken?: string | null,
  service?: FunctionService,
): Promise<TResponse | undefined> {
  const url = buildFunctionUrl(route, service);
  let response: Response;
  try {
    response = await fetch(url, {
      method: "DELETE",
      headers: buildHeaders(accessToken, service, true),
    });
  } catch (err) {
    const errMsg = err instanceof Error ? err.message : String(err);
    throw new ApiError(`Der Dienst ist nicht erreichbar: ${url} (${errMsg})`, { isNetwork: true, url, payload: err });
  }

  const result = await parseJsonResponse<TResponse & ApiErrorPayload>(response);

  if (!response.ok) {
    throw rejected(result, response, url);
  }

  // A 204 without a body is a valid answer here as well - the caller falls back to its
  // own wording then.
  return typeof result === "string" || result === undefined ? undefined : (result as TResponse);
}

/**
 * GET against a url built with {@link buildFunctionUrl}. The service has to come along: the url
 * no longer carries the host key, and the key header is picked by the app it addresses. It is
 * required for that reason - left out, the request would quietly carry the shared key or none.
 */
export async function getJson<TResponse>(
  url: string,
  accessToken: string | null | undefined,
  service: FunctionService,
): Promise<TResponse> {
  let response: Response;
  try {
    response = await fetch(url, {
      headers: buildHeaders(accessToken, service, false),
    });
  } catch (err) {
    const errMsg = err instanceof Error ? err.message : String(err);
    throw new ApiError(`Der Dienst ist nicht erreichbar: ${url} (${errMsg})`, { isNetwork: true, url, payload: err });
  }

  const result = await parseJsonResponse<TResponse & ApiErrorPayload>(response);

  if (!response.ok) {
    throw rejected(result, response, url);
  }

  if (!result) {
    throw new ApiError("Response body is empty.", { status: response.status, url });
  }

  if (typeof result === "string") {
    // Unexpected non-JSON success response
    throw new ApiError(result, { serverMessage: result, status: response.status, url });
  }

  return result as TResponse;
}

export function getUsersDirectoryUrl(): string {
  // Absolute endpoint overrides pass through unchanged; the default resolves
  // against the accounts base. The endpoint uses AuthorizationLevel.Function like
  // every other route, so the caller hands "accounts" to getJson for the key header.
  const endpoint = import.meta.env.VITE_USERS_ENDPOINT?.trim() || "users";
  return buildFunctionUrl(endpoint, "accounts");
}